import { DndContext, PointerSensor, TouchSensor, rectIntersection, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useCan } from "components";
import { SortableItem, SortableOverlay } from "modules/PendingChecklistsDashboard/components/InterventionChecklists/components/InterventionChecklistsModal/components";
import { useReorderInterventionPendingChecklistsMutation } from "modules/PendingChecklistsDashboard/components/InterventionChecklists/components/InterventionChecklistsModal/components/SortableList/hooks";
import "modules/PendingChecklistsDashboard/components/InterventionChecklists/components/InterventionChecklistsModal/components/SortableList/SortableList.scss";
import { checkIfAannameChecklistsAreAtTheTop } from "modules/PendingChecklistsDashboard/util";

import type { DragEndEvent } from "@dnd-kit/core";
import type { Checklist } from "models";
import type { SortableInterventionPendingChecklists } from "modules/PendingChecklistsDashboard/components/InterventionChecklists/components/InterventionChecklistsModal";
import type { Dispatch, SetStateAction } from "react";

type SortableListProps = {
  sortableInterventionPendingChecklists: SortableInterventionPendingChecklists[];
  checklists?: Checklist[];
  onClose: () => void;
  setSortableInterventionPendingChecklists: Dispatch<SetStateAction<SortableInterventionPendingChecklists[]>>;
};

export const SortableList = ({ sortableInterventionPendingChecklists, checklists, setSortableInterventionPendingChecklists, onClose }: SortableListProps) => {
  const { t } = useTranslation();

  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 100 } }));

  const canReorderInterventionPendingChecklist = useCan("update", "intervention-keywords-pending-checklist");
  const { mutateAsync: reorderInterventionPendingChecklists } = useReorderInterventionPendingChecklistsMutation();

  const handleOnDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    try {
      const newIndex = sortableInterventionPendingChecklists.findIndex(item => item.id === over.id);
      const oldIndex = sortableInterventionPendingChecklists.findIndex(item => item.id === active.id);
      const sortedInterventionPendingChecklists = arrayMove(sortableInterventionPendingChecklists, oldIndex, newIndex).map((i, idx) => ({ ...i, ordering: idx + 1 }));

      if (checklists && !checkIfAannameChecklistsAreAtTheTop(sortedInterventionPendingChecklists, checklists)) {
        toast.error(t("v8_invalid_order_aanname_checklists_must_be_at_the_top_of_the_list", "Invalid order: Aanname checklists must be at the top of the list"));
        return;
      }

      setSortableInterventionPendingChecklists(sortedInterventionPendingChecklists);
      await reorderInterventionPendingChecklists(sortedInterventionPendingChecklists);
    } catch (error) {
      setSortableInterventionPendingChecklists(sortableInterventionPendingChecklists);
      console.error(error);
    }
  };

  return (
    <div className="InterventionPendingChecklistsSortableList">
      <DndContext
        sensors={sensors}
        onDragEnd={handleOnDragEnd}
        collisionDetection={rectIntersection}
        modifiers={[restrictToVerticalAxis, restrictToWindowEdges, snapCenterToCursor]}
      >
        <>
          <SortableContext items={sortableInterventionPendingChecklists} disabled={!canReorderInterventionPendingChecklist}>
            <ul className="check-container">
              {sortableInterventionPendingChecklists.map(item => (
                <SortableItem
                  key={`pending-checklist-${item.checklist_id}-${item.keyword}-${item.vin_prefix}`}
                  item={item}
                  checklists={checklists}
                  sortableInterventionPendingChecklists={sortableInterventionPendingChecklists}
                  onClose={onClose}
                />
              ))}
            </ul>
          </SortableContext>
          <SortableOverlay />
        </>
      </DndContext>
    </div>
  );
};
